import { Check, Plus } from 'lucide-react';
import { useState } from 'react';
import { ROLE_LABELS } from '@cw/shared';
import { useMembers } from '@/lib/members';
import { cn } from '@/lib/utils';
import { Avatar, AvatarStack } from './avatar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type Person = { id: string; name: string; avatarUrl: string | null };

/** Seleção de pessoas da equipe interna (responsáveis, colaboradores). */
export function PeoplePicker({
  value,
  onChange,
  label = 'Adicionar',
  disabled,
}: {
  value: Person[];
  onChange: (ids: string[]) => void;
  label?: string;
  disabled?: boolean;
}) {
  const members = useMembers();
  const [q, setQ] = useState('');
  const ids = value.map((v) => v.id);
  const people = (members.data ?? []).filter(
    (m) => m.status === 'active' && m.name.toLowerCase().includes(q.toLowerCase()),
  );
  const toggle = (id: string) => onChange(ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]);
  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className="flex min-h-8 items-center gap-2 rounded-control px-1.5 py-1 text-left text-[13px] hover:bg-hover disabled:pointer-events-none"
          aria-label={label}
        >
          {value.length ? (
            <>
              <AvatarStack
                people={value.map((v) => ({ name: v.name, src: v.avatarUrl }))}
                size="xs"
                max={4}
              />
              <span className="truncate">
                {value.length === 1 ? value[0]!.name : `${value.length} pessoas`}
              </span>
            </>
          ) : (
            <span className="flex items-center gap-1.5 text-fg-subtle">
              <Plus className="size-3.5" /> {label}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar pessoa"
          className="mb-1 h-9 w-full rounded-[8px] bg-canvas/60 px-2.5 text-[13.5px] outline-none placeholder:text-fg-subtle"
        />
        <ul className="max-h-64 overflow-y-auto">
          {people.map((m) => (
            <li key={m.userId}>
              <button
                type="button"
                onClick={() => toggle(m.userId)}
                className="flex h-10 w-full items-center gap-2.5 rounded-[8px] px-2 text-left text-[13.5px] hover:bg-hover"
              >
                <Avatar name={m.name} src={m.avatarUrl} size="xs" />
                <span className="min-w-0 flex-1 truncate">{m.name}</span>
                <span className="text-[11.5px] text-fg-subtle">{ROLE_LABELS[m.role]}</span>
                <Check className={cn('size-4 text-signal', !ids.includes(m.userId) && 'invisible')} />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
